/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { combineReducers } from 'redux';
import { SagaIterator } from 'redux-saga';
import { all, takeEvery, put, call, take, race, select, delay, takeLatest } from 'redux-saga/effects';
import {
  Time,
  ActionRequest,
  ActionSuccess,
  ActionFail,
  XError,
  RecordingsConfigs,
  DestructuredDuration,
  TimeUtils,
  DurationUtils,
  Ducks,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';
import * as SingleRecordings from './singleRecordings';
import * as SeriesRecordings from './seriesRecordings';

export enum ActionTypes {
  LOAD = 'recordings/LOAD',
  SUCESS = 'recordings/SUCESS',
  FAIL = 'recordings/FAIL',
  RESET = 'recordings/RESET',
}

interface SuccessActionPayload {
  successfulLoadRequestTime?: Time;
}

type Request = ActionRequest<ActionTypes.LOAD>;
type Success = ActionSuccess<ActionTypes.SUCESS, SuccessActionPayload>;
type Fail = ActionFail<ActionTypes.FAIL>;
type Reset = ActionSuccess<ActionTypes.RESET>;

type Actions = Request | Success | Fail | Reset;

const Actions = {
  load: (): Request => ({
    type: ActionTypes.LOAD,
  }),
  sucess: (successfulLoadRequestTime: Time | undefined): Success => ({
    type: ActionTypes.SUCESS,
    payload: {
      successfulLoadRequestTime,
    },
  }),
  fail: (error: XError): Fail => ({
    type: ActionTypes.FAIL,
    error,
  }),
  reset: (): Reset => ({
    type: ActionTypes.RESET,
  }),
};

export const ActionCreators = {
  load: (): Request => Actions.load(),
};

export interface ReducerState {
  processing: boolean;
  processed: boolean;
  lastSuccessfulLoadRequest?: Time;
  error?: XError;
}

const initialState: ReducerState = {
  processed: false,
  processing: false,
};

export function status(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionTypes.LOAD:
      return {
        ...state,
        processing: true,
        processed: false,
      };
    case ActionTypes.SUCESS: {
      return {
        ...state,
        processing: false,
        processed: true,
        lastSuccessfulLoadRequest: action.payload.successfulLoadRequestTime,
      };
    }
    case ActionTypes.FAIL:
      return {
        ...state,
        processing: false,
        processed: true,
        error: action.error,
      };
    case ActionTypes.RESET:
      return initialState;
    default:
      return state;
  }
}

// SELECTORS
export const Selectors = {
  getLastSuccessfulLoadRequest: (state: StoreState): Time | undefined =>
    state.recordings.status.lastSuccessfulLoadRequest,
  isRecordingsProcessing: (state: StoreState): boolean => state.recordings.status.processing,
  isRecordingsProcessed: (state: StoreState): boolean => state.recordings.status.processed,
  getError: (state: StoreState): XError | undefined => state.recordings.status.error,
};

function* loadSingleRecordings(nowDate?: Time): SagaIterator {
  yield put(SingleRecordings.ActionCreators.load(nowDate));
  yield race([take(SingleRecordings.ActionType.UPDATE), take(SingleRecordings.ActionType.FAIL)]);
}

function* loadSeriesRecordings(): SagaIterator {
  yield put(SeriesRecordings.ActionCreators.load());
  yield race([take(SeriesRecordings.ActionType.UPDATE), take(SeriesRecordings.ActionType.FAIL)]);
}

function* loadAllRecordings(): SagaIterator {
  try {
    const { allRefresh }: RecordingsConfigs = yield select(Ducks.Dms.Selectors.getRecordings);
    const cachingRefresh: DestructuredDuration | undefined = yield select(Ducks.Dms.Selectors.getCachingRefresh);
    const lastSuccessfulLoadRequest: Time | undefined = yield select(Selectors.getLastSuccessfulLoadRequest);
    const nowDate = TimeUtils.now();

    let actionSucessPayload: Time | undefined;

    if (lastSuccessfulLoadRequest && cachingRefresh) {
      const allRefreshDate = TimeUtils.addDestructuredDuration(lastSuccessfulLoadRequest, allRefresh);
      const cachingRefreshDate = TimeUtils.addDestructuredDuration(lastSuccessfulLoadRequest, cachingRefresh);

      const shouldRefreshAll = TimeUtils.isBefore(allRefreshDate, nowDate);
      const shouldRefresh30Days = TimeUtils.isBefore(cachingRefreshDate, nowDate);

      if (shouldRefreshAll || shouldRefresh30Days) {
        yield all([call(loadSingleRecordings, !shouldRefreshAll ? nowDate : undefined), call(loadSeriesRecordings)]);
        actionSucessPayload = nowDate;
      }
    } else {
      yield all([call(loadSingleRecordings), call(loadSeriesRecordings)]);
      actionSucessPayload = nowDate;
    }

    yield put(Actions.sucess(actionSucessPayload || lastSuccessfulLoadRequest));
  } catch (error) {
    yield put(Actions.fail(XError.fromError(error)));
  }
}

function* scheduleLoadAllRecordings(): SagaIterator {
  const cachingRefresh: DestructuredDuration | undefined = yield select(Ducks.Dms.Selectors.getCachingRefresh);
  if (cachingRefresh) {
    while (true) {
      yield delay(DurationUtils.destructuredDurationtoMilliseconds(cachingRefresh));
      yield call(loadAllRecordings);
    }
  }
}

function* initRecordingsRequestSaga(): SagaIterator {
  yield call(loadAllRecordings);
  yield call(scheduleLoadAllRecordings);
}

function* resetRecordings(): SagaIterator {
  yield all([
    put(Actions.reset()),
    put(SingleRecordings.ActionCreators.reset()),
    put(SeriesRecordings.ActionCreators.reset()),
  ]);
}

// Index Saga
export function* saga(): SagaIterator {
  yield all([
    takeEvery(ActionTypes.LOAD, initRecordingsRequestSaga),
    takeLatest(Ducks.Credentials.ActionTypes.LOGOUT_SUCCESS, resetRecordings),
  ]);
}

// combine all the consuption reducers
export default combineReducers({
  single: SingleRecordings.default,
  series: SeriesRecordings.default,
  status,
});

export const sagas = [call(saga), call(SingleRecordings.saga), call(SeriesRecordings.saga)];
